import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, IMAGE_URL } from "src/network/endpoints";
import { useSelector } from "react-redux";

const ParticipantSelectionDialog = ({
  open,
  onClose,
  onConfirm,
  selectedParticipantIds = [], // IDs of users already in the group (to disable)
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]); // IDs of newly selected users
  const [activeTab, setActiveTab] = useState(0); // 0: followers, 1: linked, 2: beemz
  const [selectAll, setSelectAll] = useState(false);
  const currentUser = useSelector((state) => state.auth.user);

  // Fetch followers
  const { data: followersData, isLoading: isLoadingFollowers } = useQuery(
    ["followers", currentUser?.userId],
    () => {
      return axiosInstance.get(ApiEndPoints.USER.followerList);
    },
    {
      enabled: Boolean(open && currentUser?.userId),
    }
  );

  // Fetch linked users
  const { data: linkedUsersData, isLoading: isLoadingLinked } = useQuery(
    ["linkedUsers", currentUser?.userId],
    () => {
      return axiosInstance.get(ApiEndPoints.USER.getLinkedUsers);
    },
    {
      enabled: Boolean(open && currentUser?.userId),
    }
  );

  // Fetch beemz users (global search) - only when search term is provided
  const { data: beemzUsersData, isLoading: isLoadingBeemz } = useQuery(
    ["beemzUsers", searchTerm],
    () => {
      return axiosInstance.get(
        `${ApiEndPoints.TEAM_LINKING.getBemmzUser}?search=${encodeURIComponent(searchTerm)}`
      );
    },
    {
      enabled: Boolean(open && searchTerm.trim().length > 0),
    }
  );

  // Get current user list based on active tab
  const getCurrentUserList = () => {
    if (activeTab === 0) {
      // Followers - try multiple possible data structures
      const data = followersData?.data?.data?.data || 
                   followersData?.data?.data || 
                   followersData?.data || 
                   [];
      return Array.isArray(data) ? data : [];
    } else if (activeTab === 1) {
      // Linked users - try multiple possible data structures
      const data = linkedUsersData?.data?.data?.data?.data || 
                   linkedUsersData?.data?.data?.data || 
                   linkedUsersData?.data?.data || 
                   linkedUsersData?.data || 
                   [];
      return Array.isArray(data) ? data : [];
    } else {
      // Beemz users - try multiple possible data structures
      const data = beemzUsersData?.data?.data?.data?.data || 
                   beemzUsersData?.data?.data?.data || 
                   beemzUsersData?.data?.data || 
                   beemzUsersData?.data || 
                   [];
      return Array.isArray(data) ? data : [];
    }
  };

  // Filter users based on search term (only for followers and linked users)
  const currentUserList = getCurrentUserList();
 
  const filteredUsers =
    activeTab === 2
      ? currentUserList // Beemz users are already filtered by search
      : Array.isArray(currentUserList) && currentUserList.length > 0
      ? currentUserList.filter(
          (user) =>
            !searchTerm.trim() || // If no search term, show all
            user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];
  
  // Check if user is already a member of the group
  const isUserAlreadyMember = useCallback((userId) => {
    return selectedParticipantIds.includes(userId);
  }, [selectedParticipantIds]);

  // Handle user selection
  const handleUserSelect = (userId) => {
    // Don't allow selecting users who are already members
    if (isUserAlreadyMember(userId)) {
      return;
    }
    
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter((id) => id !== userId));
    } else {
      setSelectedIds([...selectedIds, userId]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all from current tab
      const currentUserIds = filteredUsers.map((user) => user._id);
      setSelectedIds(selectedIds.filter((id) => !currentUserIds.includes(id)));
    } else {
      // Select all from current tab, excluding users already in the group
      const userIdsToSelect = filteredUsers
        .map((user) => user._id)
        .filter((id) => !selectedIds.includes(id) && !isUserAlreadyMember(id));
      setSelectedIds([...selectedIds, ...userIdsToSelect]);
    }
    setSelectAll(!selectAll);
  };

  // Update select all state when selection changes
  useEffect(() => {
    if (filteredUsers.length > 0) {
      // Only consider users that can be selected (not already members)
      const selectableUsers = filteredUsers.filter(
        (user) => !isUserAlreadyMember(user._id)
      );
      const allSelected =
        selectableUsers.length > 0 &&
        selectableUsers.every((user) => selectedIds.includes(user._id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedIds, filteredUsers, isUserAlreadyMember]);

  // Handle confirm
  const handleConfirm = () => {
    // Get all selected users from all tabs (only newly selected, not existing members)
    const allUsers = [
      ...(followersData?.data?.data?.data || []),
      ...(linkedUsersData?.data?.data?.data?.data || []),
      ...(beemzUsersData?.data?.data?.data?.data || []),
    ];
    // Filter and deduplicate by user ID
    const userMap = new Map();
    allUsers.forEach((user) => {
      if (selectedIds.includes(user._id) && !isUserAlreadyMember(user._id)) {
        // Use Map to ensure only one entry per user ID
        if (!userMap.has(user._id)) {
          userMap.set(user._id, user);
        }
      }
    });
    const selectedUsers = Array.from(userMap.values());
    onConfirm(selectedUsers); // Pass user objects (only newly selected, deduplicated)
    onClose();
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds([]); // Reset to empty - only track newly selected users
      setSearchTerm("");
      setSelectAll(false);
      setActiveTab(0);
    }
  }, [open]);

  // Reset search when tab changes for beemz users
  useEffect(() => {
    if (activeTab === 2) {
      setSearchTerm("");
    }
  }, [activeTab]);

  const isUserSelected = (userId) => selectedIds.includes(userId);
  
  // Disable checkbox if user is already a member
  const isCheckboxDisabled = (userId) => isUserAlreadyMember(userId);
  
  const isLoading =
    (activeTab === 0 && isLoadingFollowers) ||
    (activeTab === 1 && isLoadingLinked) ||
    (activeTab === 2 && isLoadingBeemz);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          select participants
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                textTransform: "lowercase",
                fontWeight: 500,
                fontSize: "14px",
                minHeight: 48,
              },
            }}
          >
            <Tab label="followers" />
            <Tab label="linked users" />
            <Tab label="beemz users" />
          </Tabs>
        </Box>

        {/* Search Bar */}
        <Box sx={{ px: 2, py: 2 }}>
          <TextField
            fullWidth
            placeholder={
              activeTab === 2
                ? "search beemz users..."
                : "search by name or username..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#666" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
          {activeTab === 2 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              enter a search term to find beemz users
            </Typography>
          )}
        </Box>

        {/* Select All - Only show if there are selectable users */}
        {filteredUsers.filter((user) => !isUserAlreadyMember(user._id)).length > 0 && (
          <>
            <Box sx={{ px: 2, pb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    sx={{
                      color: "#6200EE",
                      "&.Mui-checked": {
                        color: "#6200EE",
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: "#6200EE", fontWeight: 500 }}>
                    select all
                  </Typography>
                }
              />
            </Box>
            <Divider />
          </>
        )}

        {/* User List */}
        <Box sx={{ maxHeight: "400px", overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : activeTab === 2 && !searchTerm.trim() ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography color="textSecondary">
                enter a search term to find beemz users
              </Typography>
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography color="textSecondary">
                No {activeTab === 0 ? "followers" : activeTab === 1 ? "linked users" : "beemz users"} found
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredUsers.map((user) => (
                <ListItem
                  key={user._id}
                  sx={{
                    px: 2,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={
                        user.image?.profilePic
                          ? `${IMAGE_URL}${user.image.profilePic}`
                          : null
                      }
                      sx={{
                        bgcolor: "gray",
                        width: 40,
                        height: 40,
                      }}
                    >
                      {user.fullName?.charAt(0)?.toUpperCase() || "U"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            opacity: isCheckboxDisabled(user._id) ? 0.5 : 1,
                          }}
                        >
                          {user.fullName || user.userName || "Unknown User"}
                        </Typography>
                        {isCheckboxDisabled(user._id) && (
                          <Chip
                            label="already added"
                            size="small"
                            sx={{
                              fontSize: "10px",
                              height: "20px",
                              backgroundColor: "#e0e0e0",
                              color: "#666",
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      user.userName
                        ? `@${user.userName}`
                        : user.email
                        ? user.email
                        : ""
                    }
                    secondaryTypographyProps={{
                      fontSize: "12px",
                      color: "#666",
                      sx: {
                        opacity: isCheckboxDisabled(user._id) ? 0.5 : 1,
                      },
                    }}
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      checked={isUserSelected(user._id)}
                      onChange={() => handleUserSelect(user._id)}
                      disabled={isCheckboxDisabled(user._id)}
                      sx={{
                        color: "#6200EE",
                        "&.Mui-checked": {
                          color: "#6200EE",
                        },
                        "&.Mui-disabled": {
                          opacity: 0.5,
                        },
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Selection Summary */}
        {selectedIds.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              selected: {selectedIds.length} participant(s)
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selectedIds.slice(0, 3).map((userId) => {
                const user = [
                  ...(followersData?.data?.data?.data || []),
                  ...(linkedUsersData?.data?.data?.data?.data || []),
                  ...(beemzUsersData?.data?.data?.data?.data || []),
                ].find((u) => u._id === userId);
                return user ? (
                  <Chip
                    key={userId}
                    label={user.fullName || user.userName}
                    size="small"
                    sx={{ fontSize: "11px" }}
                  />
                ) : null;
              })}
              {selectedIds.length > 3 && (
                <Chip
                  label={`+${selectedIds.length - 3} more`}
                  size="small"
                  sx={{ fontSize: "11px" }}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedIds.length === 0}
          sx={{
            textTransform: "lowercase",
            borderRadius: "8px",
            backgroundColor: "#6200EE",
            px: 4,
            py: 1,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#7b1fa2",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          select ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParticipantSelectionDialog;
